'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { HeartIcons, BicycleIcon } from '@/components/atoms/Icons';
import Toggle from '@/components/atoms/Toggle';
import LeaderBoard from '@/assets/images/leaderboard-bg.png';
import { CrownIcon, HeartPOHIcon } from '@/components/atoms/Icons';
import MoLeaderBoard from '@/assets/images/hearts-side-decoration.webp';
import BgLeaderBoard from '@/assets/images/main-transparent.png';
import MoBgLeaderBoard from '@/assets/images/bg-leaderboard-mobile.png';
import UIButton from '@/components/atoms/UIButtons/UIButton';
import LoginModal from '@/components/auth/loginModal';
import Style from './Leaderboard.module.css';
function getContrastColor(bgHex) {
  if (!bgHex) return '#000000';
  const hex = bgHex.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  const srgb = [r, g, b].map(c => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
  const contrastWithWhite = 1.05 / (L + 0.05);
  const contrastWithBlack = (L + 0.05) / 0.05;

  // WCAG AA threshold for small text = 4.5
  if (contrastWithWhite >= 4.5) return '#FFFFFF';
  if (contrastWithBlack >= 4.5) return '#000000';

  // strict fallback (rare case)
  return contrastWithWhite > contrastWithBlack ? '#FFFFFF' : '#000000';
}
const HEART_MASK_DATA_URI = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path fill="black" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
    </svg>`);
function formatCompactNumber(value) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString('en-US');
}
function initialsFromName(name) {
  const safe = String(name || '').trim();
  if (!safe) return '?';
  const parts = safe.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  if (parts.length === 1) {
    return first.toUpperCase();
  }
  const last = parts[parts.length - 1]?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}
function swatchColorFromId(id) {
  const palette = ['#D022BC', '#288575', '#176C85', '#F6773D', '#8B39C1', '#16A34A', '#22C55E', '#84CC16', '#F59E0B', '#F97316', '#FB7185', '#EC4899', '#DB2777', '#8B5CF6', '#6366F1', '#2563EB', '#0EA5E9', '#14B8A6', '#10B981', '#A3E635', '#FACC15', '#EAB308', '#E11D48', '#9333EA', '#0F766E', '#1D4ED8', '#DC2626'];
  const input = String(id ?? '');
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = hash * 31 + input.charCodeAt(i) >>> 0;
  }
  return palette[hash % palette.length];
}
export default function LeaderboardPage(t0) {
  const $ = _c(66);
  const {
    data,
    mode: t1,
    isAuthed: t2,
    userRankData: t3
  } = t0;
  const mode = t1 === undefined ? "heart-hunter" : t1;
  const isAuthed = t2 === undefined ? false : t2;
  const userRankData = t3 === undefined ? null : t3;
  let t4;
  if ($[0] !== data?.podium) {
    t4 = data?.podium ?? [];
    $[0] = data?.podium;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const podium = t4;
  let t5;
  if ($[2] !== data?.rows) {
    t5 = data?.rows ?? [];
    $[2] = data?.rows;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const rows = t5;
  const prevCursor = data?.pageInfo?.prevCursor ?? null;
  const nextCursor = data?.pageInfo?.nextCursor ?? null;
  const router = useRouter();
  const searchParams = useSearchParams();
  const hasAutoScrolledRef = useRef(false);
  let t6;
  if ($[4] !== router || $[5] !== searchParams) {
    t6 = tab => {
      const params = new URLSearchParams(searchParams?.toString());
      params.set("tab", tab);
      params.delete("cursor");
      params.delete("dir");
      const query = params.toString();
      router.push(query ? `/leaderboard?${query}` : "/leaderboard");
    };
    $[4] = router;
    $[5] = searchParams;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleTabChange = t6;
  const youRowId = isAuthed ? "leaderboard-you-row" : null;
  let t7;
  bb0: {
    if (prevCursor) {
      t7 = prevCursor;
      break bb0;
    }
    if (!isAuthed || mode !== "heart-hunter") {
      t7 = null;
      break bb0;
    }
    const firstRank = rows?.[0]?.rank;
    if (typeof firstRank === "number" && Number.isFinite(firstRank) && firstRank > 1) {
      t7 = String(firstRank);
      break bb0;
    }
    t7 = null;
  }
  const prevCursorForUI = t7;
  let t8;
  if ($[7] !== isAuthed || $[8] !== mode || $[9] !== youRowId) {
    t8 = () => {
      if (!isAuthed || mode !== "heart-hunter" || hasAutoScrolledRef.current) {
        return;
      }
      const el = youRowId ? document.getElementById(youRowId) : null;
      if (!el) {
        return;
      }
      hasAutoScrolledRef.current = true;
      el.scrollIntoView({
        block: "center"
      });
    };
    $[7] = isAuthed;
    $[8] = mode;
    $[9] = youRowId;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isAuthed || $[12] !== mode || $[13] !== rows.length || $[14] !== youRowId) {
    t9 = [isAuthed, mode, youRowId, rows.length];
    $[11] = isAuthed;
    $[12] = mode;
    $[13] = rows.length;
    $[14] = youRowId;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  useEffect(t8, t9);
  const [showLoginModal, setShowLoginModal] = useState(false);
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="fixed top-0 h-[1px] inset-0 z-[-2] pointer-events-none"><Image src={MoBgLeaderBoard} alt="" aria-hidden="true" className="object-cover w-full h-auto lg:hidden block print:hidden min-h-[100dvh]" /><Image src={BgLeaderBoard} alt="" aria-hidden="true" className="object-cover w-full h-auto lg:block hidden min-h-[100dvh]" /></div>;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="absolute right-0 top-0 pointer-events-none lg:w-[21.25vw] w-[38vw] z-0"><Image src={LeaderBoard} alt="" aria-hidden="true" priority={true} sizes="100vw" className="lg:block hidden ml-auto w-full h-auto" /><Image src={MoLeaderBoard} alt="" aria-hidden="true" priority={true} sizes="100vw" className="lg:hidden block w-full h-auto" /></div>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <h1 className="font-semibold sm:text-center text-left lg:mb-9 mb-3 lg:pt-24 pt-15 2xl:text-[68px] xl:text-[54px] lg:text-[48px] sm:text-[40px] text-[26px] leading-[1] text-white">Leaderboard</h1>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [{
      label: "Heart Hunter",
      value: "heart-hunter"
    }, {
      label: "by Heart",
      value: "by-heart"
    }];
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== handleTabChange || $[21] !== mode) {
    t14 = <div>{t12}<Toggle key={mode} className="mx-auto !p-0.5 !bg-[#353535]" options={t13} defaultValue={mode} onChange={handleTabChange} toggleClass="lg:!leading-[1.25em]" variant="primary" /></div>;
    $[20] = handleTabChange;
    $[21] = mode;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== mode || $[24] !== podium) {
    t15 = <Podium podium={podium} mode={mode} />;
    $[23] = mode;
    $[24] = podium;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== isAuthed || $[27] !== mode || $[28] !== prevCursorForUI) {
    t16 = isAuthed && mode === "heart-hunter" && prevCursorForUI && <div className="lg:pb-4 pb-3 flex justify-center"><Link href={{
        query: {
          tab: mode,
          cursor: prevCursorForUI,
          dir: "prev"
        }
      }} className="w-full border-1 border-white leading-[1.1] bg-black max-w-fit rounded-full px-3.5 lg:px-5 py-2 lg:py-2.5 text-center text-sm lg:text-base  font-semibold text-white hover:bg-black/90">Load Previous</Link></div>;
    $[26] = isAuthed;
    $[27] = mode;
    $[28] = prevCursorForUI;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== mode || $[31] !== rows || $[32] !== youRowId) {
    let t18;
    if ($[34] !== mode || $[35] !== youRowId) {
      t18 = (row, idx) => {
        const heartColor = swatchColorFromId(row.userId);
        const initialColor = getContrastColor(heartColor);
        return mode === "by-heart" ? <li key={`${row.heartSlug || row.heartTitle || row.heartName}-${idx}`} className="relative flex gap-4 pr-3 py-3"><div className="w-13 px-2 shrink-0 h-auto flex items-center justify-center border-r border-[#6D6D6D] text-base font-semibold text-white">{formatCompactNumber(row.rank ?? idx + 1)}</div><div className="relative shrink-0 flex items-center"><HeartThumb src={row?.thumbnailUrl || "/images/heart-color-icon.svg"} alt={row.heartTitle || row.heartName || ""} /></div><div className="min-w-0 flex-1 xl:pl-2 2xl:pl-3 pl-0 flex flex-col justify-center"><Link href={row.heartSlug ? `/hearts/${encodeURIComponent(row.heartSlug)}` : "/hearts"} className="truncate block xl:text-lg lg:text-base text-[15px] mb-0.5 leading-[1] font-semibold text-white underline-offset-2 hover:underline">{row.heartTitle || row.heartName}</Link>{(row?.heartCommunity || row?.heartState) && <p className="truncate text-xs lg:text-sm leading-[1] text-[#BCBCBC]">{[row.heartCommunity, row.heartState].filter(Boolean).join(", ")}</p>}</div><div className="text-right flex flex-col"><span className="xl:tex-xl lg:text-lg text-base font-semibold leading-[1] text-white">{formatCompactNumber(row.totalHeartVisited)}</span><p className="lg:text-sm text-xs font-normal text-[#BCBCBC] mt-0.5 leading-[1.1]">Visits</p></div></li> : <li key={row.userId} id={row.isYou ? youRowId : undefined} className="relative flex gap-4 pr-3 py-3">{row.isYou && <span className="absolute -top-2 right-[70px] left-auto rounded-full bg-[#00EAFD] px-2 py-0.5 text-[11px] font-semibold text-black">You</span>}<div className="w-13 px-2 shrink-0 h-auto flex items-center justify-center border-r border-[#6D6D6D] text-base font-semibold text-white">{formatCompactNumber(row.rank)}</div><div className="relative h-8 w-8 shrink-0"><HeartIcons className="h-8 w-8" style={{
              color: heartColor
            }} /><span className="absolute inset-0 leading-[1] pb-0.5 grid place-items-center left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-normal" style={{
              color: initialColor,
              background: heartColor
            }}>{initialsFromName(row.displayName)}</span></div><div className="min-w-0 flex-1 xl:pl-2 2xl:pl-3 pl-0 flex flex-col justify-center"><p className="truncate xl:text-lg lg:text-base text-[15px] mb-0.5 leading-[1] font-semibold text-white flex items-center gap-1.5">{row.displayName}{row.isCyclist && <BicycleIcon className="h-4 w-4 text-white shrink-0" />}</p>{row.location && <div className="truncate text-xs lg:text-sm leading-[1] text-[#BCBCBC]">{row.location}</div>}</div><div className="text-right flex flex-col"><span className="xl:tex-xl lg:text-lg text-base font-semibold leading-[1] text-white">{formatCompactNumber(row.heartCount)}</span><p className="lg:text-sm text-xs font-normal text-[#BCBCBC] mt-0.5 leading-[1.1]">Hearts</p></div></li>;
      };
      $[34] = mode;
      $[35] = youRowId;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    t17 = rows.map(t18);
    $[30] = mode;
    $[31] = rows;
    $[32] = youRowId;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[37] !== isAuthed || $[38] !== mode || $[39] !== userRankData) {
    t18 = isAuthed && userRankData && mode === "heart-hunter" && (() => {
      const youRow = userRankData;
      const heartColor_0 = swatchColorFromId(youRow.userId);
      const initialColor_0 = getContrastColor(heartColor_0);
      return <li className="flex gap-4 pr-3 py-3 sticky bottom-0 bg-[#00EAFD]"><div className="w-13 px-2 shrink-0 h-auto flex items-center justify-center border-r border-[#25BECB] text-base font-semibold text-black">{formatCompactNumber(youRow.rank)}</div><div className="relative h-8 w-8 shrink-0"><HeartIcons className="h-8 w-8" style={{
            color: heartColor_0
          }} /><span className="absolute inset-0 leading-[1] pb-0.5 grid place-items-center left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-normal" style={{
            color: initialColor_0,
            background: heartColor_0
          }}>{initialsFromName(youRow.displayName)}</span></div><div className="min-w-0 flex-1 xl:pl-2 2xl:pl-3 pl-0 flex flex-col justify-center"><p className="truncate xl:text-lg lg:text-base text-[15px] mb-0.5 leading-[1] font-semibold text-black flex items-center gap-1.5">{youRow.displayName}{youRow.isCyclist && <BicycleIcon className="h-4 w-4 text-black shrink-0" />}</p>{youRow.location && <div className="truncate text-xs lg:text-sm leading-[1] text-[#494949]">{youRow.location}</div>}</div><div className="text-right flex flex-col"><span className="xl:tex-xl lg:text-lg text-base font-semibold leading-[1] text-black">{formatCompactNumber(youRow.heartCount)}</span><p className="lg:text-sm text-xs font-normal text-[#494949] mt-0.5 leading-[1.1]">Hearts</p></div></li>;
    })();
    $[37] = isAuthed;
    $[38] = mode;
    $[39] = userRankData;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== isAuthed || $[42] !== mode) {
    t19 = !isAuthed && mode === "heart-hunter" && <li className="flex gap-4 pr-3 py-3 sticky bottom-0 bg-[#00EAFD]"><div className="sm:w-30 w-20 px-2 shrink-0 h-auto flex items-center justify-center text-base font-semibold text-black">Join the Hunt</div><UIButton variant="default" className="w-full !bg-black hover:!bg-black/80 hover:!text-white min-h-10 !cursor-pointer " onClick={() => setShowLoginModal(true)}>Login or Create Account</UIButton></li>;
    $[41] = isAuthed;
    $[42] = mode;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t17 || $[45] !== t18 || $[46] !== t19) {
    t20 = <div className="border border-white/20"><ul className="divide-y divide-white/20">{t17}{t18}{t19}</ul></div>;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== mode || $[49] !== nextCursor) {
    t21 = nextCursor ? <div className="flex justify-center pt-3"><Link href={{
        query: {
          tab: mode,
          cursor: nextCursor,
          dir: "next"
        }
      }} className=" w-full border-1 border-white leading-[1.1] bg-black max-w-fit rounded-full px-3.5 lg:px-5 py-2 lg:py-2.5 text-center text-sm lg:text-base font-semibold text-white hover:bg-black/90">Load More</Link></div> : <div className="pt-0" />;
    $[48] = mode;
    $[49] = nextCursor;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t16 || $[52] !== t20 || $[53] !== t21) {
    t22 = <div className="min-w-0 2xl:pt-14 xl:pt-10 lg:pt-6 pt-4 lg:mx-0 -mx-4">{t16}{t20}{t21}</div>;
    $[51] = t16;
    $[52] = t20;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t15 || $[56] !== t22) {
    t23 = <section className="mx-auto mt-4 grid max-w-[1200px] 2xl:gap-10 lg:gap-6 gap-4 lg:grid-cols-[1fr_582px]">{t15}{t22}</section>;
    $[55] = t15;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t14 || $[59] !== t23) {
    t24 = <div className="container-wrap relative z-[1]">{t14}{t23}</div>;
    $[58] = t14;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== showLoginModal) {
    t25 = showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />;
    $[61] = showLoginModal;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== t24 || $[64] !== t25) {
    t26 = <main className={`xl:pb-20 pb-15 relative border-t border-[#ffffff]/60 ${Style.leaderBoardwrapper}`}>{t10}{t11}{t24}{t25}</main>;
    $[63] = t24;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  return t26;
}
function HeartThumb(t0) {
  const $ = _c(10);
  const {
    src,
    alt,
    wrapperClassName,
    imageSize: t1
  } = t0;
  const imageSize = t1 === undefined ? "32px" : t1;
  const t2 = `relative h-8 w-8 shrink-0 ${wrapperClassName || ""}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      WebkitMaskImage: `url("${HEART_MASK_DATA_URI}")`,
      maskImage: `url("${HEART_MASK_DATA_URI}")`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskSize: "100% 100%",
      maskSize: "100% 100%",
      WebkitMaskPosition: "center",
      maskPosition: "center"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== alt || $[2] !== imageSize || $[3] !== src) {
    t4 = src ? <Image src={src} alt={alt} fill={true} sizes={imageSize} className="object-cover" /> : null;
    $[1] = alt;
    $[2] = imageSize;
    $[3] = src;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="absolute inset-0 overflow-hidden bg-white/10 shadow-[0_0_0_2px_rgba(255,255,255,0.20)_inset]" style={t3}>{t4}</div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t5) {
    t6 = <div className={t2}>{t5}</div>;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function Podium(t0) {
  const $ = _c(19);
  const {
    podium,
    mode
  } = t0;
  let t1;
  if ($[0] !== podium) {
    t1 = podium.find(_temp) ?? podium[0] ?? null;
    $[0] = podium;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const first = t1;
  let t2;
  if ($[2] !== podium) {
    t2 = podium.find(_temp2) ?? podium[1] ?? null;
    $[2] = podium;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const second = t2;
  let t3;
  if ($[4] !== podium) {
    t3 = podium.find(_temp3) ?? podium[2] ?? null;
    $[4] = podium;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const third = t3;
  let t4;
  if ($[6] !== mode || $[7] !== second) {
    t4 = <PodiumCard rank={2} row={second} tone="cyan" HeartClass="w-[100px] h-auto" CardClass="pt-19" mode={mode} />;
    $[6] = mode;
    $[7] = second;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== first || $[10] !== mode) {
    t5 = <PodiumCard rank={1} row={first} tone="gold" HeartClass="w-[120px] h-auto" mode={mode} />;
    $[9] = first;
    $[10] = mode;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== mode || $[13] !== third) {
    t6 = <PodiumCard rank={3} row={third} tone="green" HeartClass="w-[100px] h-auto" CardClass="pt-28" mode={mode} />;
    $[12] = mode;
    $[13] = third;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <div className="grid grid-cols-3 items-start lg:gap-3 gap-2.5 max-w-[420px] w-full mx-auto h-fit">{t4}{t5}{t6}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function _temp3(p_1) {
  return p_1.rank === 3;
}
function _temp2(p_0) {
  return p_0.rank === 2;
}
function _temp(p) {
  return p.rank === 1;
}
function PodiumCard({
  rank,
  row,
  tone,
  HeartClass,
  CardClass,
  mode
}) {
  const name = mode === 'by-heart' ? row?.heartName ?? '—' : row?.displayName ?? '—';
  const count = mode === 'by-heart' ? row?.totalHeartVisited ?? 0 : row?.heartCount ?? 0;
  const location = mode === 'by-heart' ? [row?.heartCommunity, row?.heartState].filter(Boolean).join(', ') ?? '' : row?.location ?? '';
  const initials = initialsFromName(name);
  const swatch = rank === 1 ? '#F7CB30' : rank === 2 ? '#00EAFD' : rank === 3 ? '#73C429' : swatchColorFromId(row?.userId ?? name);
  const toneClass = tone === 'gold' ? 'from-[#F7CB30]/50 border-[#F7CB30]' : tone === 'green' ? 'from-[#73C429]/75 border-[#73C429]' : 'from-[#00EAFD]/75 md:from-[#00EAFD]/30 border-[#00EAFD]';
  return <div className={`text-center text-white ${CardClass}`}>
      {rank === 1 && <div className="flex justify-center mb-1.5">
          <CrownIcon className="h-6 w-6 text-[#F7CB30]" />
        </div>}
      {mode === 'by-heart' ? <div className="relative shrink-0  flex justify-center">
          <HeartThumb src={row?.thumbnailUrl || '/images/heart-color-icon.svg'} alt={row?.heartTitle || row?.heartName || ''} wrapperClassName={rank === 1 ? 'w-34 h-34' : 'w-28 h-28'} imageSize={rank === 1 ? '250px' : '180px'} />
        </div> : <div className="relative mx-auto flex justify-center mb-3.5">
          <HeartIcons className={HeartClass} style={{
        color: swatch
      }} />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center text-xl font-extrabold text-black" style={{
        background: swatch
      }}>
            {initials}
          </span>
        </div>}
      <h2 className="text-white font-semibold lg:text-base md:text-sm text-[13px] leading-[1] truncate max-w-full" title={'Full Name: ' + name}>
        {name}
      </h2>
      {location && <div className="md:text-[13px] text-[10px] text-[#BCBCBC] mt-2 leading-[1] truncate max-w-full" title={'Location: ' + location}>
          {location}
        </div>}
      {!location && mode === 'by-heart' && <div className="md:text-[13px] text-[10px] text-[#BCBCBC] mt-2 leading-[1] truncate max-w-full">
          &nbsp;
        </div>}
      <div className={`xl:mt-7.5 md:mt-6 mt-5 relative md:py-6 py-5 px-5 bg-gradient-to-b ${toneClass} to-transparent rounded-[10px] border-t-[5px]`}>
        <div className="relative z-[1]">
          <h3 className="text-3xl font-semibold">{formatCompactNumber(count)}</h3>
          <p className="lg:mt-1 leading-[1] text-xs text-white [text-shadow:0_4px_4px_rgba(0,0,0,0.25)]">
            {mode === 'by-heart' ? 'Visits' : 'Hearts'}
          </p>

          <div className="xl:mt-4 md:mt-3 mt-2.5 lg:pb-4 pb-2 lg:text-base md:text-sm text-xs text-center font-semibold text-white leading-[1]">
            <HeartPOHIcon className="mt-4 mx-auto" style={{
            color: swatch
          }} aria-hidden />
            <span className="block mt-2">{rank === 1 ? '1st' : rank === 2 ? '2nd' : '3rd'}</span>
          </div>
        </div>
      </div>
    </div>;
}